import i18n from '@/js/i18n';

import {
  arrInAppMobile,
  arrInAppMojPlus,
  containsProductSource,
} from '@/helpers/ProductSourceHelpers';

export function getLanguages() {
  return process.env.VUE_APP_LANGUAGES.split(' ');
}

export function removeCurrentLanguage(url, currentLanguage) {
  return url.replace(new RegExp(`/${currentLanguage}($|/)`), '/');
}

// Ne doit être appelée que du module i18n
// Utiliser i18n.locale au lieu de getCurrentLanguage()
export function getCurrentLanguage() {
  const languages = getLanguages();

  // eslint-disable-next-line no-restricted-syntax
  for (const language of languages) {
    if (new RegExp(`/${language}($|/)`).test(window.location.pathname)) {
      return language;
    }
  }

  return languages[0];
}

export function getRouterBasename() {
  const nodeName = process.env.VUE_APP_SITE_BASENAME;
  const languages = getLanguages();
  const { pathname } = window.location;

  if (pathname.indexOf(nodeName) > -1) {
    return pathname.replace(new RegExp(`${nodeName}.*`), '') + nodeName;
  }

  const currentLanguage = getCurrentLanguage();

  return languages[0] === currentLanguage ? '/' : `/${currentLanguage}`;
}

export function getVersion(path) {
  return new URLSearchParams(path).get('mgnlVersion');
}

const PATH_MAGNOLIA_AUTHOR = '/magnoliaAuthor';
const PATH_MAGNOLIA_PUBLIC = '/magnoliaPublic';

export const inIframe = typeof window !== 'undefined' ? window.self !== window.top : false;
export const inMgnlIframe = inIframe && window.location.search.indexOf('mgnlPreview') > -1;
export const inEditor = inIframe && window.location.search.indexOf('mgnlPreview=false') > -1;
export const inMgnlAuthor = window.location.href.indexOf(PATH_MAGNOLIA_AUTHOR) > -1;
export const inMgnlPublic = window.location.href.indexOf(PATH_MAGNOLIA_PUBLIC) > -1;
export const inMgnl = inMgnlAuthor || inMgnlPublic;

// Déterminer si dans une App mobile (Loteries, MOJ, etc...)
export function inAppMobile() {
  return containsProductSource(arrInAppMobile);
}

// Déterminer si dans l'App mobile MOJ+ (Mise-o-Jeu Plus)
export function inMojPlusApp() {
  return containsProductSource(arrInAppMojPlus);
}

// Déterminer si dans une App mobile iOS
export function inIOSApp() {
  return inAppMobile() && /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function getHomePageUrl() {
  // Rediriger vers la page d'accueil (TTP 13297)
  return inAppMobile()
    ? i18n.t('hyperlien.appActionConnexion')
    : i18n.t('hyperlien.portailLotoQuebec');
}

export function hasPromoSelected() {
  return getOfferSelectedFromQueryParams() !== null && getOfferSelectedFromQueryParams() === '1';
}

export function getOfferSelectedFromQueryParams() {
  return getQueryParamsValue('os');
}

export function hasCodePromo() {
  return getPromoFromQueryParams() !== null && !hasPromoSelected();
}

export function getPromoFromQueryParams() {
  return getQueryParamsValue('promo');
}

// Retourne le bon base url + contexte en fonction de l'environnement
// "path" doit avoir "/" au début
export function getUrlContext(path) {
  let domainContext = window.location.origin;
  let devMode = false;
  if (domainContext.indexOf('.espacejeux.local') >= 0) {
    domainContext += PATH_MAGNOLIA_AUTHOR;
    devMode = true;
  } else if (domainContext.startsWith('http://localhost')) {
    devMode = true;
    domainContext += inMgnlAuthor ? PATH_MAGNOLIA_AUTHOR : PATH_MAGNOLIA_PUBLIC;
  }
  // Pointer sur le CDN

  // TODO: plus tard faire de meme pour tous les delivery pour optimiser le temps de reponse
  // On passe par le service jel-api pour le rest delivery de produitCasino
  if (!devMode && !inMgnlAuthor && path.startsWith('/.rest/delivery/jel/produitCasino')) {
    // Cet url sera re-ecrit dans le valve pour qu'il soit égale /jel-api/v1/jcr-delivery
    path = '/.rest/delivery/jel-api/v1/produit';
  }

  if (
    !devMode &&
    !inMgnlAuthor &&
    process.env.VUE_APP_PROXY_URL_POD.match(/.com|.ta/gi) &&
    path.match(/\/dam\/|\/.imaging\//gi)
  ) {
    // Activer la config CDN en mode PrePROD et PROD seulement
    domainContext = process.env.VUE_APP_PORTAL_DOMAIN;
  }

  return domainContext + path;
}

// Retourne le bon base url + contexte en fonction de l'environnement
// "partialPath": chemin réel. Si début par "/" alors path absolu.
// "partialPathMock": chemin sur mock=true ou dans l'éditeur Magnolia, sans "/"
export function getApiUrlContext(partialPath, partialPathMock) {
  if (process.env.VUE_APP_IS_MOCK == 'true' || inMgnl) {
    return getUrlContext(`${process.env.VUE_APP_JEL_API}/${partialPathMock}`);
  }

  if (partialPath.charAt(0) == '/') {
    return partialPath;
  }

  return `${process.env.VUE_APP_BASE_REST_SERVICE}/${partialPath}`;
}

// Determine si la page est dans un contexte mobile
export function isJelMobile() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/**
 * Retourne le lien externe en fonction de l'env. courant
 *
 * @param {string} lienExterne lien sous la forme de //www.espacejeux.com ou https://aaa.bbb.com
 *
 * @return {string} Retourne l'url
 */
export function nettoyerLienExterne(lienExterne) {
  if (
    lienExterne.indexOf('.espacejeux.com') !== -1 ||
    lienExterne.indexOf('.lotoquebec.com') !== -1
  ) {
    const hostSplit = window.location.host.split('.');
    const lienExterneSplit = lienExterne.split('.');

    if (lienExterneSplit.length == 3) {
      if (window.location.host.endsWith('.org')) {
        // Insertion de l'env. (ex: 'dev-21')
        lienExterneSplit.splice(1, 0, hostSplit[1]);
        lienExterne = lienExterneSplit.join('.');
      } else if (window.location.host.endsWith('.dev')) {
        // Insertion de l'env. (ex: 'espacejeux-01') dev
        lienExterneSplit.splice(1, 1, hostSplit[1]);
        lienExterne = lienExterneSplit.join('.');
      }
    }
    lienExterne = lienExterne.replace('.com', `.${hostSplit[hostSplit.length - 1]}`);
  }

  return lienExterne;
}

/**
 * Wrapper pour toutes les promesses
 */
export function promiseWrapper(promise, milliseconde) {
  const timeout = new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject('EXPIRE');
    }, milliseconde);
  });
  return Promise.race([promise, timeout]);
}

/**
 * Methode pour récupérer la valeur d'un QueryParam
 * @param {*} name
 */
export function getQueryParamsValue(name) {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has(name)) {
    return urlParams.get(name);
  }
  return null;
}

/**
 * Vérifier si un QueryParam existe dans l'url
 * @param {*} name
 */
export function hasQueryParam(name) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(name);
}

/**
 * Ajouter un QueryParam à l'url
 * @param {*} name
 * @param {*} value
 */
export function addQueryParam(name, value) {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set(name, value);
}

/**
 * Retirer un QueryParam de l'url
 * @param {*} name
 */
export function removeQueryParam(name) {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete(name);
  window.history.replaceState({}, '', `${window.location.pathname}`);
}

/**
 * Effectuer un post vers un url
 * @param {*} path
 * @param {*} params
 */
export function postToUrl(path, params) {
  const form = document.createElement('form');
  form.setAttribute('method', 'POST');
  form.setAttribute('action', path);
  Object.keys(params).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const hiddenField = document.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', key);
      hiddenField.setAttribute('value', params[key]);
      form.appendChild(hiddenField);
    }
  });
  document.body.appendChild(form);
  form.submit();
  form.reset();
  return false;
}
